import { isPossiblePhoneNumber } from 'libphonenumber-js';

import { guard } from 'utils/general';

const DIGITS_ONLY_REGEX = /\D/g;

export const normalizePhoneDigits = value => {
  return String(value || '').replace(DIGITS_ONLY_REGEX, '');
};

export const constructE164PhoneNumber = ({ countryCallingCode, nationalNumber } = {}) => {
  const trimmedNationalNumber = String(nationalNumber || '').trim();
  if (trimmedNationalNumber.startsWith('+')) {
    const formattedE164Digits = normalizePhoneDigits(trimmedNationalNumber);
    return formattedE164Digits ? `+${formattedE164Digits}` : undefined;
  }

  const formattedCountryCallingCode = normalizePhoneDigits(countryCallingCode);
  const formattedNationalNumber = normalizePhoneDigits(nationalNumber);

  if (!formattedCountryCallingCode || !formattedNationalNumber) {
    return undefined;
  }

  return `+${formattedCountryCallingCode}${formattedNationalNumber}`;
};

export const checkIsPossibleInternationalPhoneNumber = ({ countryCallingCode, nationalNumber } = {}) => {
  const e164PhoneNumber = constructE164PhoneNumber({ countryCallingCode, nationalNumber });
  if (!e164PhoneNumber) {
    return false;
  }

  const isPossible = guard(() => isPossiblePhoneNumber(e164PhoneNumber), false);
  if (isPossible) {
    return true;
  }

  // If user pasted an international number without "+" (e.g. "44...") while also selecting "+44",
  // treat it as an international number ONLY when it actually validates.
  const formattedNationalNumber = normalizePhoneDigits(nationalNumber);
  const formattedCountryCallingCode = normalizePhoneDigits(countryCallingCode);
  if (formattedNationalNumber && formattedCountryCallingCode && formattedNationalNumber.startsWith(formattedCountryCallingCode)) {
    const e164FromNationalAsInternational = `+${formattedNationalNumber}`;
    if (guard(() => isPossiblePhoneNumber(e164FromNationalAsInternational), false)) {
      return true;
    }
  }

  // Common UX: users include a trunk prefix "0" (e.g. UK "020...") even though E.164 omits it.
  // Try again by trimming leading zeros from the provided national number.
  const nationalNumberWithoutLeadingZeros = formattedNationalNumber.replace(/^0+/, '');

  if (!formattedCountryCallingCode || !nationalNumberWithoutLeadingZeros) {
    return false;
  }

  const e164WithoutLeadingZeros = `+${formattedCountryCallingCode}${nationalNumberWithoutLeadingZeros}`;
  return guard(() => isPossiblePhoneNumber(e164WithoutLeadingZeros), false);
};

